/**
 * CareSop 实例 Hook (用户端)
 * 包含乐观更新逻辑
 */

import { useCallback, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import * as careSopService from "@/services/careSopInstanceService";
import type { CareSopInstance, CareSopInstanceStep } from "@/types/care-sop";

interface UseCareSopInstanceOptions {
  assessmentId: string;
  enabled?: boolean;
}

interface UseCareSopInstanceReturn {
  careSop: CareSopInstance | undefined;
  isLoading: boolean;
  error: Error | null;
  toggleStep: (stepIndex: number, isCompleted: boolean) => void;
  isToggling: boolean;
  progress: {
    total: number;
    completed: number;
    percentage: number;
    display: string;
  };
}

// Query Keys
export const careSopInstanceKeys = {
  all: ["careSopInstance"] as const,
  assessment: (assessmentId: string) =>
    [...careSopInstanceKeys.all, assessmentId] as const,
};

/**
 * 使用 CareSop 实例 Hook
 */
export function useCareSopInstance({
  assessmentId,
  enabled = true,
}: UseCareSopInstanceOptions): UseCareSopInstanceReturn {
  const queryClient = useQueryClient();

  // 获取实例
  const query = useQuery({
    queryKey: careSopInstanceKeys.assessment(assessmentId),
    queryFn: () => careSopService.getCareSopInstance(assessmentId),
    enabled: enabled && !!assessmentId,
    staleTime: 30 * 1000, // 30 秒
  });

  // 切换步骤完成状态 (含乐观更新)
  const toggleStepMutation = useMutation({
    mutationFn: ({
      stepIndex,
      isCompleted,
    }: {
      stepIndex: number;
      isCompleted: boolean;
    }) =>
      careSopService.toggleStepCompletion(assessmentId, stepIndex, isCompleted),
    onMutate: async ({ stepIndex, isCompleted }) => {
      // 1. 取消正在进行的查询
      await queryClient.cancelQueries({
        queryKey: careSopInstanceKeys.assessment(assessmentId),
      });

      // 2. 保存之前的数据用于回滚
      const previousCareSop = queryClient.getQueryData<CareSopInstance>(
        careSopInstanceKeys.assessment(assessmentId),
      );

      // 3. 乐观更新：立即修改缓存中的数据
      queryClient.setQueryData<CareSopInstance>(
        careSopInstanceKeys.assessment(assessmentId),
        (old: CareSopInstance | undefined) => {
          if (!old) return old;
          const newSteps = [...old.steps];
          newSteps[stepIndex] = {
            ...newSteps[stepIndex],
            is_completed: isCompleted,
          };
          return { ...old, steps: newSteps };
        },
      );

      return { previousCareSop };
    },
    onError: (err, vars, context) => {
      // 4. 失败回滚
      if (context?.previousCareSop) {
        queryClient.setQueryData(
          careSopInstanceKeys.assessment(assessmentId),
          context.previousCareSop,
        );
      }
      toast.error("更新失败，请重试");
    },
    onSettled: () => {
      // 5. 最终刷新
      queryClient.invalidateQueries({
        queryKey: careSopInstanceKeys.assessment(assessmentId),
      });
    },
  });

  // 计算进度
  const progress = useMemo(() => {
    if (!query.data?.steps) {
      return { total: 0, completed: 0, percentage: 0, display: "0 / 0 (0%)" };
    }
    const total = query.data.steps.length;
    const completed = query.data.steps.filter((s) => s.is_completed).length;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
    return {
      total,
      completed,
      percentage,
      display: `${completed} / ${total} (${percentage}%)`,
    };
  }, [query.data]);

  return {
    careSop: query.data,
    isLoading: query.isLoading,
    error: query.error as Error | null,
    toggleStep: useCallback(
      (stepIndex: number, isCompleted: boolean) => {
        toggleStepMutation.mutate({ stepIndex, isCompleted });
      },
      [toggleStepMutation],
    ),
    isToggling: toggleStepMutation.isPending,
    progress,
  };
}

/**
 * 进度计算工具函数
 */
export function calculateProgress(steps: CareSopInstanceStep[]): {
  total: number;
  completed: number;
  percentage: number;
  display: string;
} {
  if (!steps || steps.length === 0) {
    return { total: 0, completed: 0, percentage: 0, display: "0 / 0 (0%)" };
  }
  const total = steps.length;
  const completed = steps.filter((s) => s.is_completed).length;
  const percentage = Math.round((completed / total) * 100);
  return {
    total,
    completed,
    percentage,
    display: `${completed} / ${total} (${percentage}%)`,
  };
}
